import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form943DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="FUTA Tax for 2025 | Federal Unemployment Tax Act   "
          description="Learn about FUTA, how to accurately calculate FUTA tax, and make timely deposits. So, you can avoid IRS penalties and stay focused on growing your business."
          keywords=""
          canonical="resources/employment-tax/futa-tax/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Understanding the Federal Unemployment <br />
          Tax Act{' '}
          <span className="highlight-container">
            <span className="highlight">(FUTA) </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Filing your Form 940 is important to keep your business <br />
          compliant with federal regulations.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=futatax`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/futa-tax/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    FUTA Tax at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {
                          'FUTA (Federal Unemployment Tax Act) is a federal payroll tax paid by employers to help fund unemployment compensation programs for eligible workers. Unlike '
                        }
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/fica-tax/"
                          sx={{ color: '#0202ff' }}
                        >
                          FICA
                        </Link>
                        {' taxes, '}
                        <b>FUTA is paid only by the employer</b>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must Pay FUTA Tax:</b>
                        {' Employers'} {'pay FUTA tax if they paid '}
                        <b>$1,500 or more in wages in any calendar quarter</b>
                        {' this year or last '}
                        year or
                        {
                          ' had at least one employee work on some part of a day in '
                        }
                        <b>20 or more different weeks</b>
                        {' during the year.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>FUTA Tax Rate:</b>
                        {' The standard FUTA tax rate is '}
                        <b>6.0%</b>
                        {' on the first '}
                        <b>$7,000</b>
                        {
                          " of each employee's annual wages. Employers may receive a credit of up to "
                        }
                        <b>5.4%</b>
                        {
                          ' for qualifying state unemployment tax payments, potentially reducing the effective rate to '
                        }
                        <b>0.6%</b>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Filing Form:</b>
                        {' Employers report FUTA tax annually using '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-940-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 940</b>
                        </Link>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-940-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>.</b>
                        </Link>
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <b>FUTA Deposit Schedule:</b>
                        {' FUTA tax deposits are required '}
                        <b>
                          quarterly when the FUTA tax liability exceeds $500
                        </b>
                        {'. If the liability is $500 or less, it can '}
                        generally be
                        {' carried forward to the next quarter.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        <b>FUTA vs. FICA:</b>
                        {
                          ' FUTA funds unemployment programs and is paid only by employers, while FICA funds Social Security and Medicare and is generally shared by employers and employees.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="7" />
                      <span itemProp="name">
                        <b>FUTA vs. SUTA:</b>
                        {' FUTA is a '}
                        <b>federal</b>
                        {' unemployment tax, while SUTA is a '}
                        <b>state-level</b>
                        {
                          ' unemployment tax used to fund state unemployment benefits. Timely SUTA payments may '
                        }
                        qualify
                        {' an employer for a FUTA tax credit.'}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The Federal Unemployment Tax Act is a key part of the U.S.
                  payroll tax system. It requires employers to pay taxes that
                  fund unemployment benefits for workers who lose their jobs.
                  Unlike Social Security and Medicare taxes, only employers are
                  responsible for paying FUTA; employees do not contribute to
                  it.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Whether you are a business owner or payroll manager,
                  understanding FUTA tax is essential for ensuring compliance
                  and avoiding penalties.
                </Typography>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is FUTA Tax?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who pays FUTA Tax?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#futa" sx={{ color: '#0202ff' }}>
                          FUTA Tax Rate and Wage limit
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing FUTA: Form 940
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When should I deposit FUTA Tax?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Calculate FUTA Tax?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why does FUTA compliance matter?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#futa-vs-fica" sx={{ color: '#0202ff' }}>
                          FUTA vs FICA
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#futa-vs-suta"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          FUTA vs SUTA
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  What is FUTA Tax?
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    FUTA is also known as the Federal Unemployment Tax Act. It
                    is a U.S. federal law that requires employers to pay a
                    payroll tax (unemployment tax). These taxes are used to fund{' '}
                    <b>unemployment compensation programs</b> for workers who
                    have lost their jobs.{' '}
                  </li>
                  <li>
                    {' '}
                    FUTA tax is <b>paid only by employers</b>, not by employees.
                    It supports the federal government&#39;s oversight and
                    administration of state unemployment insurance (UI)
                    programs.{' '}
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4 }}
                >
                  <b>Tips:</b> Unemployment Insurance (UI) is a government
                  program that gives temporary financial support to eligible
                  workers who lose their jobs (no fault of their own).
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-File your Form 940 the smart way with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Fast, secure, and IRS-authorized e-filing in minutes!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        id="who"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=futatax`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Who pays FUTA Tax?
                </Typography>
                <Typography variant="body1" id="key" sx={{ mb: 2 }}>
                  These are the criteria used to determine whether an employer
                  is required to pay FUTA tax,
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  For Regular Employers:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If you paid <b>$1,500 or more</b> in wages for any calendar
                    quarter this year or last year.
                  </li>

                  <Typography
                    sx={{ textAlign: 'center', fontWeight: 'bold', my: 1 }}
                  >
                    (or)
                  </Typography>

                  <li>
                    You had at least{' '}
                    <b>
                      one employee work some part of a day in 20 or more
                      different weeks
                    </b>{' '}
                    (includes full-time, part-time, and temporary employees)
                    during the year.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  For Household Employers:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If you paid a wage of{' '}
                    <b>$1,000 or more to household employees</b> in any calendar
                    quarter of this year or previous year.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  For Agricultural Employers:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If you paid a wage of <b>$20,000 or more</b> in cash for any
                    calendar quarter.
                  </li>

                  <Typography
                    sx={{ textAlign: 'center', fontWeight: 'bold', my: 1 }}
                  >
                    (or)
                  </Typography>

                  <li>
                    If you employed <b>10 or more farmworkers</b> (full-time,
                    part-time, or seasonal){' '}
                    <b> on any day for 20 or more different weeks</b> during
                    this year or previous year.
                  </li>
                </Box>
                <Alert
                  id="futa"
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0 }}
                >
                  <b>Note:</b> FUTA tax doesn&#39;t apply to employers in
                  American Samoa, Guam, and the CNMI, but it does apply to
                  employers in the USVI and Puerto Rico.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  FUTA Tax Rate and Wage limit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    The Standard FUTA tax rate is <b>6.0%</b>{' '}
                  </li>
                  <li id="filing">
                    {' '}
                    The <b>Taxable wage limit is $7,000</b> per employee
                    annually. This means you only pay FUTA tax on the first
                    $7,000 of each employee’s wages annually.{' '}
                  </li>
                  <li>
                    {' '}
                    Employers may be eligible for a FUTA{' '}
                    <b>tax credit of up to 5.4%</b> if they pay their State
                    Unemployment Tax (SUTA) in full and on time as long as their
                    state is not classified as a credit reduction state.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Filing FUTA: Form 940
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must file{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-940/"
                    sx={{ color: '#2020ff' }}
                  >
                    IRS Form 940
                  </Link>{' '}
                  annually to report your FUTA tax. The deadline to file with
                  the IRS is <b>January 31</b>. You can e-file Form 940 easily
                  using{' '}
                  <Link
                    href="https://app.taxzerone.com/signup/"
                    sx={{ color: '#2020ff' }}
                  >
                    TaxZerone.
                  </Link>
                </Typography>
                <Alert
                  id="when"
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0 }}
                >
                  <b>Note:</b> Need help with filing Form 940? Check out our
                  detailed{' '}
                  <Link
                    href="/resources/employment-tax/form-940-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 940 instructions
                  </Link>{' '}
                  for step-by-step guidance.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  When should I deposit FUTA Tax?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    FUTA tax must be deposited every quarter, if your tax
                    liability is more than $500 in a quarter.{' '}
                  </li>
                  <li>
                    {' '}
                    If it is $500 or less, you can forward it over to the next
                    quarter.{' '}
                  </li>
                  <li>
                    {' '}
                    FUTA tax can be deposited through the{' '}
                    <Link
                      href="https://www.eftps.gov/eftps/"
                      sx={{ color: '#2020ff' }}
                    >
                      EFTPS
                    </Link>{' '}
                    (Electronic Federal Tax Payment System).{' '}
                  </li>
                </Box>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Reporting Period
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Quarter 1 </TableCell>

                          <TableCell>April 30* </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Quarter 2 </TableCell>

                          <TableCell>July 31* </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Quarter 3 </TableCell>

                          <TableCell>October 31* </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Quarter 4 </TableCell>

                          <TableCell id="how">January 31* </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" sx={{ mt: 1, color: '#6f6c7f' }}>
                    *If there is a federal holiday or weekend, the next business
                    day will be considered.
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  How to Calculate FUTA Tax?
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    To calculate FUTA tax, determine the first $7,000 from
                    employee wages paid in a calendar year.{' '}
                  </li>
                  <li> Multiply that amount by 6.0%. </li>
                  <li>
                    {' '}
                    Apply the state unemployment tax credit (up to 5.4%) to
                    reduce your federal tax liability.{' '}
                  </li>
                </Box>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: '#dfe7fd',
                    p: 2,
                    borderRadius: 3,

                    border: '2px solid #3e66d4ff',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Example 1:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Three employees each earn $10,000. Employer is eligible for
                    a full 5.4% credit. Calculate the FUTA.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Calculation:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Now, the FUTA rate is 0.6% <br />
                    $7,000 × 0.006 = $42 <br />
                    Total FUTA = $42 × 3 = <b>$126</b>
                  </Typography>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 3 }}
                >
                  <b>Tips:</b> If the employee doesn’t have any credits, the
                  maximum tax is $7000 x 6% = <b>$420.</b>
                </Alert>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: '#dfe7fd',
                    p: 2,
                    borderRadius: 3,

                    border: '2px solid #3e66d4ff',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Example 2:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Let us consider an employee who earns $4,000 and qualifies
                    for full credit (0.6%). Calculate the FUTA.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Calculation:
                  </Typography>
                  <Typography id="why" variant="body1" sx={{ mb: 2 }}>
                    $4,000 × 0.006 = <b>$24</b>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Here, the employee earns less than $7000, so the entire
                    earnings will be subject to FUTA tax.
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Why does FUTA compliance matter?
                </Typography>
                <Typography variant="body1">
                  FUTA compliance is essential for:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li> Staying in good standing with the IRS </li>
                  <li id="futa-vs-fica">
                    {' '}
                    Ensuring employees can access unemployment benefits (if
                    eligible){' '}
                  </li>
                  <li> Avoiding penalties for late payments or non-filing. </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  FUTA vs FICA
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  FUTA and
                  <Link
                    href="/resources/employment-tax/fica-tax/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    FICA
                  </Link>{' '}
                  are two important payroll taxes, but they serve very different
                  purposes. FUTA funds unemployment benefits and is paid by
                  employers only, while FICA supports Social Security and
                  Medicare and is shared by both employers and employees. Here
                  is a quick difference:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Feature
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            FUTA
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            FICA
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Purpose </TableCell>

                          <TableCell>Funds unemployment benefits </TableCell>
                          <TableCell>
                            Funds Social Security and Medicare programs{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Who Pays </TableCell>
                          <TableCell>Employer only </TableCell>
                          <TableCell>Both Employer and Employee </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Applicable Wage Base </TableCell>

                          <TableCell>
                            First $7,000 of each employee’s wages per year{' '}
                          </TableCell>
                          <TableCell>
                            Social Security: up to $168,600 Medicare: no
                            limit{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Tax Rate </TableCell>
                          <TableCell>
                            6.0% (can be reduced to 0.6% with SUTA credit){' '}
                          </TableCell>
                          <TableCell>
                            6.2% for Social Security + 1.45% for Medicare (each
                            for employer and employee){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Form Used </TableCell>

                          <TableCell>
                            {' '}
                            <Link
                              href="/resources/employment-tax/form-940-instructions/"
                              sx={{ color: '#2020ff' }}
                            >
                              Form 940
                            </Link>{' '}
                            (annual){' '}
                          </TableCell>
                          <TableCell>
                            <Link
                              href="/resources/employment-tax/form-941-instructions/"
                              sx={{ color: '#2020ff' }}
                            >
                              Form 941
                            </Link>{' '}
                            (quarterly){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Deposit Requirement </TableCell>
                          <TableCell>Quarterly if over $500 </TableCell>
                          <TableCell>
                            Usually semi-weekly or monthly, depending on payroll
                            size{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow id="futa-vs-suta" sx={{}}>
                          <TableCell>Employee Deduction </TableCell>

                          <TableCell>
                            No, not deducted from employees{' '}
                          </TableCell>
                          <TableCell>
                            Yes, deducted from employee wages{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  FUTA vs SUTA
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  Both FUTA and SUTA are unemployment taxes paid by employers.
                  While FUTA is a federal tax, SUTA is a state-level tax. SUTA
                  specifically used to fund unemployment benefits for workers
                  who lose their jobs. Here is a quick difference:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Feature
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            FUTA
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            SUTA
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Level of Tax </TableCell>

                          <TableCell>Federal </TableCell>
                          <TableCell>State </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Purpose </TableCell>
                          <TableCell>
                            Supports federal unemployment programs and provides
                            aid to states{' '}
                          </TableCell>
                          <TableCell>
                            Funds state unemployment benefits directly{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Who Pays </TableCell>

                          <TableCell>Employers only </TableCell>
                          <TableCell>
                            Employers (in most states); States like Alaska, New
                            Jersey, and Pennsylvania require employee
                            contributions too{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Tax Rate </TableCell>
                          <TableCell>
                            6.0% (standard), usually reduced to 0.6% with SUTA
                            credit{' '}
                          </TableCell>
                          <TableCell>
                            Varies by state and employer experience{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Wage Base </TableCell>

                          <TableCell>
                            {' '}
                            First $7,000 of each employee’s wages
                          </TableCell>
                          <TableCell>
                            Varies by state (e.g., $7,000 to $72,800)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form Used </TableCell>
                          <TableCell>
                            Form 940 (filed annually with IRS){' '}
                          </TableCell>
                          <TableCell>
                            State-specific forms (filed quarterly or as required
                            by the state){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Deposit Frequency </TableCell>

                          <TableCell>
                            Quarterly if liability {'>'} $500{' '}
                          </TableCell>
                          <TableCell>
                            Varies by state (monthly, quarterly, etc.){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Credit Impact </TableCell>
                          <TableCell>
                            Employers can get up to <b>5.4% credit</b> for
                            timely SUTA payments which helps to lower FUTA
                            tax{' '}
                          </TableCell>
                          <TableCell>No credit applies to SUTA </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the 2025{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/p15.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Publication 15,
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/taxtopics/tc759"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          TC759
                        </Link>{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need Help with FUTA Filing?
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Staying compliant with FUTA is essential to avoid penalties and
              support employee benefits programs.
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Use TaxZerone to e-file your form quickly and securely.
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=futatax`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form943DueDates;
